import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";

import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from './content/components/ui/tooltip';

import './App.css';
// import './tailwind.css';
import Login from './content/Login';
import Header from './content/components/Header';
import Ymc from './content/Ymc';
import FleetContent from './content/components/Fleet';
import CrewContent from './content/components/Crew';
import BookingsContent from './content/components/Bookings';
import ExpensesContent, { trimStringLength } from './content/components/Expenses';
import Sidepanel from './content/components/Sidepanel';
import { ReduxUpdater } from './redux/ReduxUpdater';
import { LocalStorage } from './LocalStorage';
import ConfirmDialog from './content/dialogs/Confirm';
import { activeDialogState } from './redux/slices/dialogsSlice';
import SettingsDialog from './content/dialogs/Settings';
import DatepickerDialog from './content/dialogs/Datepicker';
import { lastAuthenticatedUserState, lastOffCanvasState, lastPrelineState } from './redux/slices/tsSlice';
import UploadContent from './content/components/Upload';
import EmployeesContent from './content/components/Employees';
import IntegrationsContent from './content/components/Integrations';
import { menuCollapsedState, selectedNavState } from './redux/slices/statusesSlice';
import OffCanvas from './content/components/elements/OffCanvas';
import { ReceiptPreview } from './content/components/elements/ReceiptPreview';
import { GlobalConstants } from './GlobalConstants';
import { ApiCalls } from './ApiCalls';
import ProgressDialog from './content/dialogs/Progress';
import ReimbursementsContent from './content/components/Reimbursements';
import UploadReceiptDialog from './content/dialogs/UploadReceipt';
import ReceiptHistoryDialog from './content/dialogs/ReceiptHistory';

async function loadPreline() {
  return import('preline/dist/index.js');
}

const LayoutShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const currentActiveDialogState =  useSelector(activeDialogState);
    const prelineRefreshTS =  useSelector(lastPrelineState);
    const location = useLocation();
    const isMenuCollapsed = useSelector(menuCollapsedState);

    React.useEffect(() => {
        console.log('INIT...');
        ReduxUpdater.currentYacht = LocalStorage.yacht;
        ReduxUpdater.selectedNav = LocalStorage.nav;
        ReduxUpdater.selectedUser = LocalStorage.user;
        ReduxUpdater.yachtCrew = LocalStorage.crew;
        // ReduxUpdater.yachtCrewCards = LocalStorage.crewCards;
        ReduxUpdater.authenticatedUser = LocalStorage.authUser;
    }, []);

    React.useEffect(() => {
        const initPreline = async () => {
            await loadPreline();

            ReduxUpdater.autoInitPreline();
        };

        initPreline();
    }, [location.pathname]);

    React.useEffect(() => {
        if (
            window.HSStaticMethods &&
            typeof window.HSStaticMethods.autoInit === 'function'
        ) {
            window.HSStaticMethods.autoInit();
        }
    }, [prelineRefreshTS]);

    const renderDialogs = React.useMemo(() => {
        console.log('currentActiveDialogState:', currentActiveDialogState);

        return (
            <>
                <ConfirmDialog />
                <DatepickerDialog />
                <SettingsDialog />
                <ProgressDialog />
                <ReceiptHistoryDialog />
                <UploadReceiptDialog />
            </>
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentActiveDialogState]);

    return (
        <div data-layer="webpage" className="Webpage w-[1262px] max-w-[100vw] h-[-webkit-fill-available] bg-white inline-flex flex-col justify-start items-start overflow-hidden">
            <Header />
            <div data-layer="heroFrame" className="Heroframe self-stretch flex-1 inline-flex justify-start items-start overflow-hidden">
                {!isMenuCollapsed && <Sidepanel />}
                {children}
            </div>
            {renderDialogs}
        </div>
    );
};

function App() {
    const authenticatedUserTS =  useSelector(lastAuthenticatedUserState);
    const selectedNav = useSelector(selectedNavState);
    const offCanvasTS =  useSelector(lastOffCanvasState);

    const [ImageURL, setImageURL] = React.useState(ReduxUpdater.offCanvas?.supabase_image_link ?? '');
    const [SignedImageURL, setSignedImageURL] = React.useState('');
    const [documentType, setDocumentType] = React.useState<'image' | 'pdf'>('image');
    const [OldSelNav, setOldSelNav] = React.useState(selectedNav);


    React.useEffect(() => {
        const handleKeyDown = (ke: KeyboardEvent) => {
            if (ke.code === 'Escape' && ReduxUpdater.offCanvas !== null) {
                ReduxUpdater.offCanvas = null;
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    React.useEffect(() => {
        if (ReduxUpdater.offCanvas && ReduxUpdater.offCanvas.supabase_image_link !== '') {
            return setImageURL(ReduxUpdater.offCanvas.supabase_image_link);
        }
        setImageURL('');
    }, [offCanvasTS]);

    React.useEffect(() => {
        console.log('setImageURL:', ImageURL);
        if (!ImageURL || ImageURL === '') {
            ReduxUpdater.documentLoadingError = true;
            return;
        }
        console.log('ImageURL:', ImageURL);
        ReduxUpdater.documentLoading = true;
        ApiCalls.getSignedUrl(ImageURL).then(url => {
            console.log('setSignedImageURL:', url);
            setDocumentType(ImageURL.endsWith('.pdf') ? 'pdf' : 'image');
            setSignedImageURL(url);
            // ReduxUpdater.imageLoading = false;
        });
    }, [ImageURL]);

    const isAuthenticated = React.useMemo(() => {
        return ReduxUpdater.authenticated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticatedUserTS]);

    const showOffCanvas = React.useMemo(() => {
        if (selectedNav !== OldSelNav) {
            ReduxUpdater.offCanvas = null;
        }
        setOldSelNav(selectedNav);
        return [1, 3].includes(selectedNav) && ReduxUpdater.offCanvas !== null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offCanvasTS, selectedNav]);

    if (!isAuthenticated) {
        return (
            <BrowserRouter>
                <Routes>
                    <Route path="*" element={<Login />} />
                </Routes>
                <ToastContainer />
            </BrowserRouter>
        );
    }

    return (
        <QueryClientProvider client={queryClient}>
            <TooltipProvider>
                <BrowserRouter>
                    <LayoutShell>
                        <Routes>
                            <Route path="*" element={<Navigate to={[GlobalConstants.UserRole.STANDARD, GlobalConstants.UserRole.BASIC].includes(ReduxUpdater.authenticatedUser?.user_role ?? '') ? '/expenses' : '/fleet'} replace={true} />} />
                            <Route path="/fleet" element={<Ymc mainContent={<FleetContent />} />} />
                            <Route path="/employees" element={<Ymc mainContent={<EmployeesContent />} />} />
                            {/* <Route path="/ymc/bookings" element={<Ymc mainContent={<CrewContent />} />} />
                            <Route path="/ymc/crew" element={<Ymc mainContent={<CrewContent />} />} /> */}
                            <Route path="/captain" element={<Login />} />
                            <Route path="/bookings" element={<Ymc mainContent={<BookingsContent />} />} />
                            <Route path="/crew" element={<Ymc mainContent={<CrewContent />} />} />
                            <Route path="/expenses" element={<Ymc mainContent={<ExpensesContent />} />} />
                            <Route path="/reimbursements" element={<Ymc mainContent={<ReimbursementsContent />} />} />
                            <Route path="/upload" element={<Ymc mainContent={<UploadContent />} />} />
                            <Route path="/integrations" element={<Ymc mainContent={<IntegrationsContent />} />} />
                        </Routes>
                        <ToastContainer />
                        {showOffCanvas && <OffCanvas>
                            <ReceiptPreview
                                src={SignedImageURL !== '' ?  SignedImageURL : GlobalConstants.receiptPlaceholder}
                                alt={ReduxUpdater.offCanvas?.user_description ?? ''}
                                type={documentType}
                                description={trimStringLength(ReduxUpdater.offCanvas?.general_item_description ?? '', 75)}
                                rounded={false}
                                allowDownload={ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT}
                                cc={ReduxUpdater.offCanvas?.pan_number}
                            />
                        </OffCanvas>}
                    </LayoutShell>
                </BrowserRouter>
            </TooltipProvider>
        </QueryClientProvider>
    );
}

export default App;
